import type { Element, ChildrenArray, Node } from 'react';
import React, { Children, cloneElement, useEffect, useState, useRef } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { remToPx } from 'Components/utils/style/style.util';
import { theme } from 'Root/tailwind.config';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
export type ActiveColEnum = 'StartCol' | 'EndCol';
export const START_COL_CMP: ActiveColEnum = 'StartCol';
export const END_COL_CMP: ActiveColEnum = 'EndCol';
const MARGIN_TOP = 0.875;
const HEADER_HEIGHT = 4;
const FIX_SCROLL_OFFSET = 2;
type colProps = {
  children: Node;
  additionalClasses?: string;
  dataTestId?: string;
  headerOffset?: number;
  // These 3 props are added during cloning below, do not add manually
  activeCol: string;
  isTablet: boolean;
  isColSticky: boolean;
};
export const StartCol = ({
  children,
  additionalClasses,
  dataTestId,
  activeCol,
  isTablet,
  isColSticky,
  headerOffset,
}: colProps) => {
  const startColRef = useRef();
  const stickyStyle =
    isTablet && isColSticky
      ? {
          position: 'fixed',
          top: `${headerOffset || 5}rem`,

          /**
           * have fun
           * comparing max-width container to half viewport size minus padding
           * react will remove calc() and expose the result for us
           * leaving min(maxWidth, halfViewport)
           */
          maxWidth: `calc(${theme.maxWidth.site} / 2)`,
          width: `calc(50vw - ${theme.padding['5']})`,
        }
      : null;
  return activeCol === START_COL_CMP || isTablet ? (
    <div
      className={classnames('mb-20 sm:w-1/2', additionalClasses, {
        'w-full': !isTablet,
      })}
      data-testid={dataTestId}
      style={stickyStyle}
      ref={startColRef}
    >
      {children}
    </div>
  ) : null;
};
export const EndCol = ({
  children,
  additionalClasses,
  dataTestId,
  activeCol,
  isTablet,
  isColSticky,
}: colProps) => {
  return activeCol === END_COL_CMP || isTablet ? (
    <div
      className={classnames('pb-40 sm:w-1/2 sm:ps-20', additionalClasses, {
        'w-full': !isTablet,
        'sm:ms-auto': isColSticky,
      })}
      data-testid={dataTestId}
    >
      {children}
    </div>
  ) : null;
};
StartCol.displayName = START_COL_CMP;
EndCol.displayName = END_COL_CMP;
type Props = {
  children: ChildrenArray<Element<typeof StartCol | typeof EndCol>>;
  initialActiveCol?: ActiveColEnum;
  leftButtonLabel: string;
  rightButtonLabel: string;
  stickyTopPosition: string;
};
export const DoubleColLayer = ({
  children,
  initialActiveCol = START_COL_CMP,
  leftButtonLabel,
  rightButtonLabel,
  stickyTopPosition,
}: Props) => {
  const { isTablet } = useResponsive();
  const [activeCol, setActiveCol] = useState(initialActiveCol);
  const [isSticky, setIsSticky] = useState(false);
  const [currentScrollY, setCurrentScrollY] = useState(0);
  const stickyHeaderButtons = useRef();
  const containerRef = useRef<HTMLDivElement | null | undefined>();
  const layer = document.querySelector('[data-testid="Layer-body"]');
  const headerHeight = remToPx(HEADER_HEIGHT); // Header height

  const marginTop = remToPx(MARGIN_TOP); // Wrapper div has a parent with a margin of 0.875rem

  const baseScrollLimit = headerHeight + marginTop;
  // Reset active col when switching inbound/outbound
  useEffect(() => {
    setActiveCol(initialActiveCol);
  }, [initialActiveCol]);
  // EasyComparison Mobile
  useEffect(() => {
    const containerElement = containerRef.current;

    const handleScrollEasyComparison = () => {
      if (activeCol === END_COL_CMP && containerElement != null) {
        setCurrentScrollY(containerElement.getBoundingClientRect().top);
      }
    };

    if (!isTablet && layer) {
      layer.addEventListener('scroll', handleScrollEasyComparison);
    }

    return () => {
      if (!isTablet && layer) {
        layer.removeEventListener('scroll', handleScrollEasyComparison);
      }
    };
  }, [activeCol]);
  // Sticky Buttons
  useEffect(() => {
    const containerElement = containerRef.current;

    const handleScroll = () => {
      if (containerElement != null) {
        // When the buttons are "fixed", it is not calculated in wrapperElement dimensions anymore
        const scrollTop =
          stickyHeaderButtons.current?.style.position === 'fixed'
            ? baseScrollLimit + stickyHeaderButtons.current.getBoundingClientRect().height
            : baseScrollLimit;
        setIsSticky(containerElement.getBoundingClientRect().top <= scrollTop);
      }
    };

    if (layer) {
      layer.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (layer) {
        layer.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);
  const childrenAsArray = Children.toArray(children);
  const StartColCmp = childrenAsArray.find(({ type }) => type.displayName === START_COL_CMP);
  const EndColCmp = childrenAsArray.find(({ type }) => type.displayName === END_COL_CMP);

  if (Children.count(children) !== 2 || !StartColCmp || !EndColCmp) {
    return null;
  }

  const isColSticky = isSticky && isTablet;
  const isHeaderSticky = isSticky && !isTablet;

  if (isHeaderSticky && stickyHeaderButtons.current) {
    stickyHeaderButtons.current.style.position = 'fixed';
    stickyHeaderButtons.current.style.top = `${stickyTopPosition}`;
  }

  if (!isHeaderSticky && stickyHeaderButtons.current) {
    stickyHeaderButtons.current.style.position = 'static';
  }

  const childrenWithProps = Children.map(children, (child) => {
    return cloneElement(child, {
      isColSticky,
      activeCol,
      isTablet,
    });
  });

  const onClickHandler = () => {
    setActiveCol(END_COL_CMP);

    if (currentScrollY && layer && stickyHeaderButtons.current) {
      // waiting for the col change Left / right
      setTimeout(() => {
        const buttons = stickyHeaderButtons.current;

        if (layer != null && buttons != null) {
          layer.scrollTo(
            0,
            Math.abs(currentScrollY) + // last scroll
              baseScrollLimit +
              buttons.getBoundingClientRect().height +
              remToPx(FIX_SCROLL_OFFSET),
          );
        }
      }, 0);
    }
  };

  return (
    <div ref={containerRef}>
      {!isTablet && (
        <div
          className={classnames('z-1 mb-40 flex', {
            'inset-x-0 my-15 border-b-1 border-lightGrey bg-white px-20 shadow-diffuse':
              isHeaderSticky,
          })}
          ref={stickyHeaderButtons}
        >
          <button
            className={classnames(
              'flex-1 border-0 border-r-1 border-solid border-grey px-20 py-5 text-14 font-bold underline',
              {
                'text-sienna no-underline': activeCol === START_COL_CMP,
              },
            )}
            onClick={() => setActiveCol(START_COL_CMP)}
            type="button"
          >
            {leftButtonLabel}
          </button>
          <button
            className={classnames('flex-1 px-20 py-5 text-14 font-bold underline', {
              'text-sienna no-underline': activeCol === END_COL_CMP,
            })}
            onClick={onClickHandler}
            type="button"
          >
            {rightButtonLabel}
          </button>
        </div>
      )}
      <div className="flex flex-row">{childrenWithProps}</div>
    </div>
  );
};
